import { useTranslate } from 'shared/lib/i18n'
import { cashCancelReasonKey } from 'shared/api'
import { Alert, AlertTitle } from 'shared/components/ui/alert'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { money, sum } from './lookup-words'
import { usePayment, type PaymentModel, type PaymentPanelProps } from './use-payment'

/**
 * The pieces of the payment on the parcel card — the fact, «Принять наличные», the
 * refusal, «Отменить оплату?» — over `usePayment` (see there for the rules).
 */
/**
 * The payment as one fact: paid or not, what is owed or what was collected — and, when
 * nothing can be taken here, why, in a short line.
 */
export function PaymentStatus({ model }: { model: PaymentModel }) {
	const { t } = useTranslate()
	const { view } = model
	return (
		<div className='flex flex-col gap-1'>
			<div className='flex flex-wrap items-center gap-2'>
				{view.paid ? (
					<Badge variant='secondary' data-testid='payment-status-paid'>
						{t('payment.statusPaid')}
					</Badge>
				) : (
					<Badge variant='outline' data-testid='payment-status-unpaid'>
						{t('payment.statusUnpaid')}
					</Badge>
				)}
				{!view.paid && model.amountOwed > 0 && (
					<span className='tabular-nums' data-testid='payment-owed'>
						{t('payment.som', { amount: sum(model.amountOwed) })}
					</span>
				)}
				{view.paid && view.collected && (
					<span data-testid='payment-collected'>
						{t('payment.collectedAmount')}{' '}
						<span className='tabular-nums' data-testid='payment-collected-amount'>
							{money(t, view.collected)}
						</span>
					</span>
				)}
			</div>
			{view.paid && !view.collected ? (
				// Paid, but nothing was collected at our counter. The partner confirmed
				// payment, so NO payment step is offered and the fee is not taken twice.
				<span className='text-xs text-muted-foreground' data-testid='payment-prepaid'>
					{t('payment.prepaidNotice')}
				</span>
			) : !view.paid && model.amountOwed <= 0 ? (
				// The server's fee is 0 — the partner's Recipient does not pay, or the
				// line says 0 (ADR-0032): there is no cash to take.
				<span className='text-xs text-muted-foreground' data-testid='payment-nothing-to-pay'>
					{t('handover.nothingToPay')}
				</span>
			) : !view.paid && !model.readOnly && model.state !== 'ON_SHELF' ? (
				<span className='text-xs text-muted-foreground' data-testid='payment-unavailable'>
					{t('payment.notOnShelf')}
				</span>
			) : null}
		</div>
	)
}

/** «Принять наличные» with the amount on it — the card's main button while cash may be taken. */
export function CollectButton({ model }: { model: PaymentModel }) {
	const { t } = useTranslate()
	if (!model.collectable) return null
	return (
		<div className='flex items-center gap-2' data-testid='payment-form'>
			<Button type='button' data-testid='payment-collect-submit' disabled={model.busy} onClick={model.submit}>
				{t('payment.collectSubmit')}
				<span className='font-normal opacity-80' data-testid='payment-amount'>
					· {t('payment.som', { amount: sum(model.amountOwed) })}
				</span>
			</Button>
		</div>
	)
}

/** A refusal of the last act, in the server's words. */
export function PaymentError({ model }: { model: PaymentModel }) {
	const { t } = useTranslate()
	if (!model.errorKey) return null
	return (
		<Alert variant='destructive' data-testid='payment-error' data-key={model.errorKey}>
			<AlertTitle>{t(model.errorKey as never)}</AlertTitle>
		</Alert>
	)
}

/** Why the cash may not be cancelled now — the server's reason, in words. */
export function CancelUnavailable({ model, className }: { model: PaymentModel; className?: string }) {
	const { t } = useTranslate()
	if (!model.cancelUnavailable) return null
	return (
		<span className={className} data-testid='payment-cancel-unavailable' data-reason={model.cancelUnavailable}>
			{t(cashCancelReasonKey(model.cancelUnavailable) as never)}
		</span>
	)
}

/** «Отменить оплату?» — the confirmation of an act that gives the cash back. */
export function CancelPaymentDialog({ model }: { model: PaymentModel }) {
	const { t } = useTranslate()
	return (
		<AlertDialog open={model.confirming} onOpenChange={(open) => !model.busy && model.setConfirming(open)}>
			<AlertDialogContent data-testid='payment-cancel-dialog'>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('payment.cancelAsk')}</AlertDialogTitle>
					<AlertDialogDescription>{t('payment.cancelConfirm')}</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={model.busy}>{t('payment.cancelBack')}</AlertDialogCancel>
					<Button
						variant='destructive'
						data-testid='payment-cancel-confirm'
						disabled={model.busy}
						aria-busy={model.busy}
						onClick={model.cancel}
					>
						{model.busy ? t('payment.cancelBusy') : t('payment.cancelSubmit')}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

/**
 * The payment on its own: the fact, «Принять наличные», «Отменить оплату» with its
 * confirmation. The card places these pieces itself (`usePayment`); this whole is the
 * seam the panel's tests drive.
 */
export function PaymentPanel(props: PaymentPanelProps) {
	const { t } = useTranslate()
	const model = usePayment(props)
	return (
		<div className='flex flex-col gap-3' data-testid='payment-panel'>
			<PaymentStatus model={model} />
			<CollectButton model={model} />
			{model.cancellable && (
				<div data-testid='payment-cancel-form'>
					<Button
						type='button'
						variant='destructive'
						data-testid='payment-cancel-submit'
						disabled={model.busy}
						onClick={model.askCancel}
					>
						{t('payment.cancelSubmit')}
					</Button>
				</div>
			)}
			<CancelUnavailable model={model} className='text-sm text-muted-foreground' />
			<PaymentError model={model} />
			<CancelPaymentDialog model={model} />
		</div>
	)
}
